import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { print } from 'graphql';
import {
  ApplyCouponToCartInput,
  BillingAddressInput,
  Cart,
  CartItemInput,
  CategoryInterface,
  Customer,
  CustomerAddressInput,
  MutationUpdateCustomerAddressArgs,
  PaymentMethodInput,
  ProductInterface,
  RemoveCouponFromCartInput,
  RemoveItemFromCartInput,
  SetShippingAddressesOnCartInput,
  UpdateCartItemsInput,
} from '../models/shop';
import { CountryData, SquareData } from '../models/shopCustom';
import {
  ADD_MUTI_PRODUCT_TO_CART_SKYTRAK,
  ADD_PRODUCT_VIRTUAL_TO_CART,
  ADD_PRODUCTS_MICRO_TRANS_BUNDLE_PACK,
  APPLY_COUPON_SKYTRAK,
  CARD_LOOKUP_MONERIS,
  CREATE_CUSTOMER_ADDRESS,
  CREATE_EMPTY_CART,
  DEACTIVE_CUSTOMER_CART,
  DELETE_CUSTOMER_ADDRESS,
  PROCESS_MPI_3D_AUTHENTICATION,
  REMOVE_COUPON_FROM_CART_SKYTRAK,
  REMOVE_ITEM_FROM_CART_SKYTRAK,
  SET_BILLING_ADDRESS_ON_CART_SKYTRAK,
  SET_PAYMENT_METHOD_AND_PLACE_ORDER,
  SET_SHIPPING_ADDRESSES_ON_CART_SKYTRAK,
  UPDATE_CART_ITEM_SQUARE_SUBSCRIPTION,
  UPDATE_CART_ITEMS_SKYTRAK,
  UPDATE_CUSTOMER_ADDRESS,
  UPDATE_QUANTITY_MINI_CART_SKYTRAK,
} from '../utils/mutations';
import {
  GET_COUNTRIES,
  GET_CUSTOMER_CART_SKYTRAK,
  GET_CUSTOMER_INFO,
  GET_PRODUCT_BY_CATEGORY_ID_RELEVANCE,
  GET_PRODUCT_MICRO_TRANSACTION,
  GET_PRODUCTS_BY_SKU_SKYTRAK,
  GET_PRODUCTS_BY_URL_KEY_SKYTRAK,
} from '../utils/queries';
import request from '../utils/request';
import { RootState, store } from '../utils/store';
import countriesData from './country-state.json';
import { signOut } from './user';

export const getProductSkytrakByCategoryId = createAsyncThunk(
  'shopSKytrak/getProductSkytrakByCategoryId',
  async (
    payload: {
      id: string[];
      pageSize: number;
      currentPage: number;
      isSkytrakGolftec: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(GET_PRODUCT_BY_CATEGORY_ID_RELEVANCE),
          variables: {
            id: payload.id,
            pageSize: payload.pageSize,
            currentPage: payload.currentPage,
            sortEnum: 'ASC',
          },
        },
        {
          params: {
            isSkytrakGolftec: payload.isSkytrakGolftec,
          },
        }
      );

      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getCustomerSkytrakInfo = createAsyncThunk(
  'shopSKytrak/getCustomerSkytrakInfo',
  async (payload: { isSkytrakGolftec: boolean }, { rejectWithValue }) => {
    try {
      const {
        data: {
          data: { customer },
        },
      } = await request.post(
        'graphql',
        {
          query: print(GET_CUSTOMER_INFO),
        },
        {
          params: {
            isSkytrakGolftec: payload.isSkytrakGolftec,
          },
        }
      );
      return customer;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const deActiveCustomerCart = createAsyncThunk(
  'shopSKytrak/deActiveCustomerCart',
  async (
    payload: { isSkytrakGolftec: boolean } | undefined,
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(DEACTIVE_CUSTOMER_CART),
        },
        {
          params: {
            isSkytrakGolftec: payload?.isSkytrakGolftec,
          },
        }
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const createEmptyCartSkytrak = createAsyncThunk(
  'shopSKytrak/createEmptyCartSkytrak',
  async (
    payload: { isSkytrakGolftec: boolean } | undefined,
    { rejectWithValue }
  ) => {
    try {
      const {
        data: { data },
      } = await request.post(
        'graphql',
        {
          query: print(CREATE_EMPTY_CART),
        },
        {
          params: {
            isSkytrakGolftec: payload?.isSkytrakGolftec,
          },
        }
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getCustomerSkytrakCart = createAsyncThunk(
  'shopSKytrak/getCustomerSkytrakCart',
  async (
    payload: { isSkytrakGolftec: boolean } | undefined,
    { rejectWithValue }
  ) => {
    try {
      const {
        data: {
          data: { customerCart },
        },
      } = await request.post(
        'graphql',
        { query: print(GET_CUSTOMER_CART_SKYTRAK) },
        {
          params: {
            isSkytrakGolftec: payload?.isSkytrakGolftec,
          },
        }
      );
      return customerCart;
    } catch (e) {
      console.log(e);
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const cardLookUpMoneris = createAsyncThunk(
  'shopSkytrak/cardLookUpMoneris',
  async (
    payload: {
      cart_id: string;
      cc_number: string;
      store_id: number;
      isSkytrakGolftec: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(CARD_LOOKUP_MONERIS),
          variables: {
            cartId: payload.cart_id,
            ccNumber: payload.cc_number,
            storeId: payload.store_id,
          },
        },
        {
          params: { isSkytrakGolftec: payload.isSkytrakGolftec },
        }
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const processMpi3DAuthentication = createAsyncThunk(
  'shopSkytrak/processMpi3DAuthentication',
  async (
    payload: {
      incrementId: string;
      cardName: string;
      ccExpMonth: string;
      ccExpYear: string;
      ccNumber: string;
      userAgent: string;
      uRL: string;
      isSkytrakGolftec: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(PROCESS_MPI_3D_AUTHENTICATION),
          variables: {
            incrementId: payload.incrementId,
            cardName: payload.cardName,
            ccExpMonth: payload.ccExpMonth,
            ccExpYear: payload.ccExpYear,
            ccNumber: payload.ccNumber,
            userAgent: payload.userAgent,
            uRL: payload.uRL,
          },
        },
        {
          params: { isSkytrakGolftec: payload.isSkytrakGolftec },
        }
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const createCustomerAddressSkytrak = createAsyncThunk(
  'shopSkytrak/createCustomerAddressSkytrak',
  async (
    payload: {
      customerAddressInput: CustomerAddressInput;
      isSkytrakGolftec: boolean;
    },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(CREATE_CUSTOMER_ADDRESS),
          variables: { customerAddressInput: payload.customerAddressInput },
        },
        {
          params: {
            isSkytrakGolftec: payload.isSkytrakGolftec,
          },
        }
      );
      if (data.errors) {
        return rejectWithValue(data.errors);
      }

      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const updateCustomerSkytrakAddress = createAsyncThunk(
  'shopSkytrak/updateCustomerSkytrakAddress',
  async (
    payload: {
      update: MutationUpdateCustomerAddressArgs;
      isSkytrakGolftec: boolean;
    },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(UPDATE_CUSTOMER_ADDRESS),
          variables: { id: payload.update.id, input: payload.update.input },
        },
        {
          params: {
            isSkytrakGolftec: payload.isSkytrakGolftec,
          },
        }
      );

      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const deleteCustomerSkytrakAddress = createAsyncThunk(
  'shopSkytrak/deleteCustomerSkytrakAddress',
  async (
    payload: { id: number; isSkytrakGolftec: boolean },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(DELETE_CUSTOMER_ADDRESS),
          variables: { id: payload.id },
        },
        {
          params: {
            isSkytrakGolftec: payload.isSkytrakGolftec,
          },
        }
      );

      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const setBillingAddressSkytrakOnCart = createAsyncThunk(
  'shopSkytrak/setBillingAddressSkytrakOnCart',
  async (
    payload: {
      billingAddressInput: BillingAddressInput;
      cartId: string;
      isSkytrakGolftec: boolean;
    },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const {
        data: {
          data: {
            setBillingAddressOnCart: { cart },
          },
        },
      } = await request.post(
        'graphql',
        {
          query: print(SET_BILLING_ADDRESS_ON_CART_SKYTRAK),
          variables: {
            billingAddress: payload.billingAddressInput,
            cartId: payload.cartId,
          },
        },
        {
          params: {
            isSkytrakGolftec: payload.isSkytrakGolftec,
          },
        }
      );
      return cart;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const setShippingAddressesSkytrakOnCart = createAsyncThunk(
  'shopSkytrak/setShippingAddressesSkytrakOnCart',
  async (
    payload: {
      input: SetShippingAddressesOnCartInput;
      isSkytrakGolftec: boolean;
    },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(SET_SHIPPING_ADDRESSES_ON_CART_SKYTRAK),
          variables: {
            input: {
              ...payload.input,
              cart_id: payload.input.cart_id,
            },
          },
        },
        {
          params: {
            isSkytrakGolftec: payload.isSkytrakGolftec,
          },
        }
      );

      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const setPaymentMethodAndPlaceOrderSkytrak = createAsyncThunk(
  'shopSkytrak/setPaymentMethodAndPlaceOrderSkytrak',
  async (
    payload: {
      cart_id: string;
      paymentInfo: PaymentMethodInput;
      card_name?: string;
      isSkytrakGolftec: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const trackSource = store.getState().user.trackSourceSkyTrak;
      console.log('trackSource', trackSource);
      const { data } = await request.post(
        'graphql',
        {
          query: print(SET_PAYMENT_METHOD_AND_PLACE_ORDER),
          variables: {
            input: {
              cart_id: payload.cart_id,
              payment_method: payload.paymentInfo,
            },
          },
        },
        {
          params: {
            isSkytrakGolftec: payload.isSkytrakGolftec,
            orderSource: trackSource,
          },
        }
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const addProductVirtualSkytrakToCart = createAsyncThunk(
  'shopSkytrak/addProductVirtualSkytrakToCart',
  async (
    payload: {
      quantity: number;
      sku: string;
      cartId: string;
      isSkytrakGolftec: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(ADD_PRODUCT_VIRTUAL_TO_CART),
          variables: {
            cartId: payload.cartId,
            quantity: payload.quantity,
            sku: payload.sku,
          },
        },
        {
          params: {
            isSkytrakGolftec: payload.isSkytrakGolftec,
          },
        }
      );
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const addProductsToCart = createAsyncThunk(
  'shopSkytrak/addProductVirtualSkytrakToCart',
  async (
    payload: {
      cartId: string;
      cartItems: CartItemInput[];
      isSkytrakGolftec: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const {
        data: {
          data: {
            addProductsToCart: { cart, user_errors },
          },
        },
      } = await request.post(
        'graphql',
        {
          query: print(ADD_MUTI_PRODUCT_TO_CART_SKYTRAK),
          variables: {
            cartId: payload.cartId,
            cartItems: payload.cartItems,
          },
        },
        {
          params: {
            isSkytrakGolftec: payload.isSkytrakGolftec,
          },
        }
      );
      return { cart, user_errors };
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const updateQuantityMiniCartSkytrak = createAsyncThunk(
  'shopSkytrak/updateQuantityMiniCartSkytrak',
  async (
    payload: {
      cartId: string;
      quantity: number;
      itemId: string;
      isSkytrakGolftec: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const {
        data: {
          data: {
            updateCartItems: { cart },
          },
        },
      } = await request.post(
        'graphql',
        {
          query: print(UPDATE_QUANTITY_MINI_CART_SKYTRAK),
          variables: {
            cartId: payload.cartId,
            quantity: payload.quantity,
            itemId: payload.itemId,
          },
        },
        {
          params: {
            isSkytrakGolftec: payload.isSkytrakGolftec,
          },
        }
      );
      return cart;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const updateItemCartSkytrak = createAsyncThunk(
  'shopSkytrak/updateItemCartSkytrak',
  async (
    payload: {
      input: UpdateCartItemsInput;
      isSkytrakGolftec: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const {
        data: {
          data: {
            updateCartItems: { cart },
          },
        },
      } = await request.post(
        'graphql',
        {
          query: print(UPDATE_CART_ITEMS_SKYTRAK),
          variables: {
            input: payload.input,
          },
        },
        {
          params: {
            isSkytrakGolftec: payload.isSkytrakGolftec,
          },
        }
      );
      return cart;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const removeItemInCartSkytrak = createAsyncThunk(
  'shopSkytrak/removeItemInCartSkytrak',
  async (
    payload: {
      input: RemoveItemFromCartInput;
      isSkytrakGolftec: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const {
        data: {
          data: {
            removeItemFromCart: { cart },
          },
        },
      } = await request.post(
        'graphql',
        {
          query: print(REMOVE_ITEM_FROM_CART_SKYTRAK),
          variables: {
            input: payload.input,
          },
        },
        {
          params: {
            isSkytrakGolftec: payload.isSkytrakGolftec,
          },
        }
      );
      return cart;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getCountries = createAsyncThunk(
  'shop/getCountries',
  async (payload: { isSkytrakGolftec: boolean }, { rejectWithValue }) => {
    try {
      const {
        data: {
          data: { countries },
        },
      } = await request.post(
        'graphql',
        {
          query: print(GET_COUNTRIES),
        },
        {
          params: {
            isSkytrakGolftec: payload.isSkytrakGolftec,
          },
        }
      );
      if (countries) {
        return countries;
      }
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getProductByUrlKeySkytrak = createAsyncThunk(
  'shopSkytrak/getProductByUrlKeySkytrak',
  async (
    payload: { url_key: string[]; isSkytrakGolftec: boolean },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(GET_PRODUCTS_BY_URL_KEY_SKYTRAK),
          variables: {
            url_key: payload.url_key,
          },
        },
        {
          params: {
            isSkytrakGolftec: payload.isSkytrakGolftec,
          },
        }
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getProductBySkuSkytrak = createAsyncThunk(
  'shopSkytrak/getProductBySkuSkytrak',
  async (
    payload: { sku: string[]; isSkytrakGolftec: boolean },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(GET_PRODUCTS_BY_SKU_SKYTRAK),
          variables: {
            sku: payload.sku,
          },
        },
        {
          params: {
            isSkytrakGolftec: payload.isSkytrakGolftec,
          },
        }
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getSimulationProductBySkuSkytrak = createAsyncThunk(
  'shopSkytrak/getSimulationProductBySkuSkytrak',
  async (
    payload: { sku: string[]; isSkytrakGolftec: boolean },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(GET_PRODUCTS_BY_SKU_SKYTRAK),
          variables: {
            sku: payload.sku,
          },
        },
        {
          params: {
            isSkytrakGolftec: payload.isSkytrakGolftec,
          },
        }
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const applyCouponSkytrak = createAsyncThunk(
  'shopSkytrak/applyCouponSkytrak',
  async (
    payload: { input: ApplyCouponToCartInput; isSkytrakGolftec: boolean },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(APPLY_COUPON_SKYTRAK),
          variables: {
            input: payload.input,
          },
        },
        {
          params: {
            isSkytrakGolftec: payload.isSkytrakGolftec,
          },
        }
      );

      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const removeCouponSkytrak = createAsyncThunk(
  'shopSkytrak/removeCouponSkytrak',
  async (
    payload: { input: RemoveCouponFromCartInput; isSkytrakGolftec: boolean },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const {
        data: {
          data: {
            removeCouponFromCart: { cart },
          },
        },
      } = await request.post(
        'graphql',
        {
          query: print(REMOVE_COUPON_FROM_CART_SKYTRAK),
          variables: {
            input: payload.input,
          },
        },
        {
          params: {
            isSkytrakGolftec: payload.isSkytrakGolftec,
          },
        }
      );

      return cart;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getProductMicroTransactionPack = createAsyncThunk(
  'shopSkytrak/getProductMicroTransactionPack',
  async (payload: { isSkytrakGolftec: boolean }, { rejectWithValue }) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(GET_PRODUCT_MICRO_TRANSACTION),
          variables: {
            transType: 'Pack',
            type: 'Course Play',
          },
        },
        {
          params: {
            isSkytrakGolftec: payload.isSkytrakGolftec,
          },
        }
      );

      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getProductMicroTransactionBundle = createAsyncThunk(
  'shopSkytrak/getProductMicroTransactionBundle',
  async (payload: { isSkytrakGolftec: boolean }, { rejectWithValue }) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(GET_PRODUCT_MICRO_TRANSACTION),
          variables: {
            transType: 'Bundle',
            type: 'Course Play',
          },
        },
        {
          params: {
            isSkytrakGolftec: payload.isSkytrakGolftec,
          },
        }
      );

      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getProductMicroTransactionSingle = createAsyncThunk(
  'shopSkytrak/getProductMicroTransactionSingle',
  async (payload: { isSkytrakGolftec: boolean }, { rejectWithValue }) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(GET_PRODUCT_MICRO_TRANSACTION),
          variables: {
            transType: 'Single',
            type: 'Course Play',
          },
        },
        {
          params: {
            isSkytrakGolftec: payload.isSkytrakGolftec,
          },
        }
      );

      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getProductMicroTransactionRange = createAsyncThunk(
  'shopSkytrak/getProductMicroTransactionRange',
  async (payload: { isSkytrakGolftec: boolean }, { rejectWithValue }) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(GET_PRODUCT_MICRO_TRANSACTION),
          variables: {
            transType: 'Single',
            type: 'Ranges',
          },
        },
        {
          params: {
            isSkytrakGolftec: payload.isSkytrakGolftec,
          },
        }
      );

      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const addProductMicroTransactionBundlePack = createAsyncThunk(
  'shopSkytrak/addProductMicroTransactionBundlePack',
  async (
    payload: {
      cartId: string;
      cartItems: CartItemInput[];
      isSkytrakGolftec: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(ADD_PRODUCTS_MICRO_TRANS_BUNDLE_PACK),
          variables: {
            cartId: payload.cartId,
            cartItems: payload.cartItems,
          },
        },
        {
          params: {
            isSkytrakGolftec: payload.isSkytrakGolftec,
          },
        }
      );

      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const switchStatusSquareSubsOnCart = createAsyncThunk(
  'shopSkytrak/switchStatusSquareSubsOnCart',
  async (
    payload: {
      cartId: string;
      itemId: number;
      holdAfterCreated: boolean;
      isSkytrakGolftec: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const {
        data: {
          data: {
            updateCartItemSquareSubscription: { cart },
          },
        },
      } = await request.post(
        'graphql',
        {
          query: print(UPDATE_CART_ITEM_SQUARE_SUBSCRIPTION),
          variables: {
            cartId: payload.cartId,
            itemId: payload.itemId,
            holdAfterCreated: payload.holdAfterCreated,
          },
        },
        {
          params: {
            isSkytrakGolftec: payload.isSkytrakGolftec,
          },
        }
      );

      return cart;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const changeStatusSubscriptionSkytrakPlan = createAsyncThunk(
  'shopSkytrak/changeStatusSubscriptionSkytrakPlan',
  async (
    payload: {
      status: 'pause' | 'resume';
      subscription_id: number;
      skytrakStoreCode: string;
      isSkytrakGolftec: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        `/rest/${payload.skytrakStoreCode}/V1/square/subscription/${payload.status}`,
        {
          subscription_id: payload.subscription_id,
        },
        {
          params: {
            isSkytrakGolftec: payload.isSkytrakGolftec,
          },
        }
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const cancelTrackingSubscription = createAsyncThunk(
  'shopSkytrak/cancelTrackingSubscription',
  async (
    payload: {
      membershipPlanId: string;
      reasonCode: string;
      detail: string;
      movingToCompetitor: boolean;
      cancelDatetime: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        `api/skytrak/membership/cancel-tracking`,
        {
          membershipPlanId: payload.membershipPlanId,
          reasonCode: payload.reasonCode,
          detail: payload.detail,
          movingToCompetitor: payload.movingToCompetitor,
          cancelDatetime: payload.cancelDatetime,
        }
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const cancelSubscriptionSquare = createAsyncThunk(
  'shopSkytrak/cancelSubscriptionSquare',
  async (
    payload: {
      subscription_id: number;
      skytrakStoreCode: string;
      isSkytrakGolftec: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        `/rest/${payload.skytrakStoreCode}/V1/square/subscription/cancel`,
        {
          subscription_id: payload.subscription_id,
        },
        {
          params: {
            isSkytrakGolftec: payload.isSkytrakGolftec,
          },
        }
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const updateCreditCardSubscription = createAsyncThunk(
  'shopSkytrak/updateCreditCardSubscription',
  async (
    payload: {
      subscription_id: number;
      skytrakStoreCode: string;
      isSkytrakGolftec: boolean;
      card_nonce: string;
      cc_exp_month: number;
      cc_exp_year: number;
      postal_code: string;
      card_type: string;
      last4Digits: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        `/rest/${payload.skytrakStoreCode}/V1/square/subscription/update-card-profile`,
        {
          subscription_id: payload.subscription_id,
          card_nonce: payload.card_nonce,
          cc_exp_month: payload.cc_exp_month,
          cc_exp_year: payload.cc_exp_year,
          postal_code: payload.postal_code,
          card_type: payload.card_type,
          last4Digits: payload.last4Digits,
        },
        {
          params: {
            isSkytrakGolftec: payload.isSkytrakGolftec,
          },
        }
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

interface ShopSkytrakState {
  dataByCategory: null | CategoryInterface[];
  customer: null | Customer;
  loadingAddress: boolean;
  cart: null | Cart;
  cartCloneSimulation: null | Cart;
  chooseCustomerAddressShippingId: null | number;
  chooseCustomerAddressBillingId: null | number;
  defaultAddress: string;
  countriesData: CountryData[] | null;
  dataProductSkytrak: null | ProductInterface[];
  squareData: SquareData | null;
  dataSimulationProductSkytrak: null | ProductInterface[];
  loadingUpdateMiniCartSkytrak: boolean;
  cartIdSkytrak: string | null;
  loadingDeactiveCart: boolean;
  loadingCreateEmptyCart: boolean;
  loadingSwitchSubscriptions: boolean;
  dataProductMicroTransPack: null | ProductInterface[];
  dataProductMicroTransBundle: null | ProductInterface[];
  dataProductMicroTransRange: null | ProductInterface[];
  dataProductMicroTransSingle: null | ProductInterface[];
}

const initialState: ShopSkytrakState = {
  dataByCategory: null,
  customer: null,
  loadingAddress: false,
  cart: null,
  cartCloneSimulation: null,
  chooseCustomerAddressShippingId: null,
  chooseCustomerAddressBillingId: null,
  defaultAddress: '',
  countriesData: countriesData.countries,
  dataProductSkytrak: null,
  squareData: null,
  dataSimulationProductSkytrak: null,
  loadingUpdateMiniCartSkytrak: false,
  cartIdSkytrak: null,
  loadingDeactiveCart: false,
  loadingCreateEmptyCart: false,
  loadingSwitchSubscriptions: false,
  dataProductMicroTransPack: null,
  dataProductMicroTransBundle: null,
  dataProductMicroTransRange: null,
  dataProductMicroTransSingle: null,
};

const shopSkytrakSlice = createSlice({
  name: 'shopSkytrak',
  initialState,
  reducers: {
    changeChooseCustomerAddressShippingSkytrakId: (state, action) => {
      state.chooseCustomerAddressShippingId = action.payload;
    },
    changeChooseCustomerAddressBillingSkytrakId: (state, action) => {
      state.chooseCustomerAddressBillingId = action.payload;
    },
    changeDefaultAddressSkytrak: (state, action) => {
      state.defaultAddress = action.payload;
    },
    changeDataSquare: (state, action) => {
      state.squareData = action.payload;
    },
    changeCartCloneSimulation: (state, action) => {
      state.cartCloneSimulation = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getProductSkytrakByCategoryId.fulfilled,
      (state, action) => {
        if (action.payload.data?.categories?.items) {
          state.dataByCategory = action.payload.data?.categories.items;
        }
      }
    );
    builder.addCase(getProductByUrlKeySkytrak.fulfilled, (state, action) => {
      if (action.payload.data?.products?.items?.length > 0) {
        state.dataProductSkytrak = action.payload.data?.products?.items;
      }
    });
    builder.addCase(getProductBySkuSkytrak.fulfilled, (state, action) => {
      if (action.payload.data?.products?.items?.length > 0) {
        state.dataProductSkytrak = action.payload.data?.products?.items;
      }
    });
    builder.addCase(
      getSimulationProductBySkuSkytrak.fulfilled,
      (state, action) => {
        if (action.payload.data?.products?.items?.length > 0) {
          state.dataSimulationProductSkytrak =
            action.payload.data?.products?.items;
        }
      }
    );
    builder.addCase(getCustomerSkytrakInfo.pending, (state, action) => {
      state.loadingAddress = true;
    });
    builder.addCase(getCustomerSkytrakInfo.fulfilled, (state, action) => {
      state.customer = action.payload;
      state.loadingAddress = false;
    });
    builder
      .addCase(deActiveCustomerCart.pending, (state, action) => {
        state.loadingDeactiveCart = true;
      })
      .addCase(deActiveCustomerCart.fulfilled, (state, action) => {
        state.loadingDeactiveCart = false;
      });
    builder
      .addCase(createEmptyCartSkytrak.pending, (state, action) => {
        state.loadingCreateEmptyCart = true;
      })
      .addCase(createEmptyCartSkytrak.fulfilled, (state, action) => {
        state.loadingCreateEmptyCart = false;
        state.cartIdSkytrak = action.payload;
        state.cart = null;
        state.cartCloneSimulation = null;
      });
    builder
      .addCase(getCustomerSkytrakCart.pending, (state) => {
        // state.loadingAddress = true;
      })
      .addCase(getCustomerSkytrakCart.fulfilled, (state, action) => {
        state.cart = action.payload;
        state.cartIdSkytrak = action.payload.id;
        // state.loadingAddress = false;
      });
    builder
      .addCase(setBillingAddressSkytrakOnCart.pending, (state, action) => {
        state.loadingAddress = true;
      })
      .addCase(setBillingAddressSkytrakOnCart.fulfilled, (state, action) => {
        state.cart = action.payload;
        state.cartIdSkytrak = action.payload.id;
        state.loadingAddress = false;
      })
      .addCase(addProductsToCart.pending, (state, action) => {
        state.loadingAddress = true;
      })
      .addCase(addProductsToCart.fulfilled, (state, action) => {
        if (action.payload.cart) {
          state.cart = action.payload.cart;
          state.cartIdSkytrak = action.payload.cart.id;
          state.loadingAddress = false;
        }
      })
      .addCase(applyCouponSkytrak.pending, (state, action) => {
        state.loadingAddress = true;
      })
      .addCase(updateQuantityMiniCartSkytrak.pending, (state, action) => {
        state.loadingUpdateMiniCartSkytrak = true;
      })
      .addCase(updateQuantityMiniCartSkytrak.fulfilled, (state, action) => {
        state.cart = action.payload;
        state.cartIdSkytrak = action.payload.id;
        state.loadingUpdateMiniCartSkytrak = false;
      })
      .addCase(updateItemCartSkytrak.pending, (state) => {
        state.loadingUpdateMiniCartSkytrak = true;
      })
      .addCase(removeItemInCartSkytrak.fulfilled, (state, action) => {
        state.cart = action.payload;
        state.cartIdSkytrak = action.payload.id;
        state.loadingUpdateMiniCartSkytrak = false;
      })
      .addCase(applyCouponSkytrak.fulfilled, (state, action) => {
        if (action.payload?.data?.applyCouponToCart?.cart) {
          state.cart = action.payload?.data?.applyCouponToCart?.cart;
          state.cartIdSkytrak =
            action.payload?.data?.applyCouponToCart?.cart.id;
        }
        state.loadingAddress = false;
      })
      .addCase(removeCouponSkytrak.pending, (state, action) => {
        state.loadingAddress = true;
      })
      .addCase(removeCouponSkytrak.fulfilled, (state, action) => {
        state.cart = action.payload;
        state.cartIdSkytrak = action.payload.id;
        state.loadingAddress = false;
      });
    builder
      .addCase(createCustomerAddressSkytrak.pending, (state, action) => {
        state.loadingAddress = true;
      })
      .addCase(createCustomerAddressSkytrak.fulfilled, (state, action) => {
        state.loadingAddress = false;
      });
    builder
      .addCase(updateCustomerSkytrakAddress.pending, (state, action) => {
        state.loadingAddress = true;
      })
      .addCase(updateCustomerSkytrakAddress.fulfilled, (state, action) => {
        state.loadingAddress = false;
      });
    builder
      .addCase(deleteCustomerSkytrakAddress.pending, (state, action) => {
        state.loadingAddress = true;
      })
      .addCase(deleteCustomerSkytrakAddress.fulfilled, (state, action) => {
        state.loadingAddress = false;
      });
    builder.addCase(getCountries.fulfilled, (state, action) => {
      if (action.payload) {
        state.countriesData = action.payload;
      } else {
        state.countriesData = countriesData.countries;
      }
  });

    builder.addCase(getCountries.rejected, (state) => {
      state.countriesData = countriesData.countries;
    });
    builder.addCase(
      getProductMicroTransactionPack.fulfilled,
      (state, action) => {
        if (action.payload.data?.products?.items?.length > 0) {
          state.dataProductMicroTransPack =
            action.payload.data?.products?.items;
        }
      }
    );
    builder.addCase(
      getProductMicroTransactionBundle.fulfilled,
      (state, action) => {
        if (action.payload.data?.products?.items?.length > 0) {
          state.dataProductMicroTransBundle =
            action.payload.data?.products?.items;
        }
      }
    );
    builder.addCase(
      getProductMicroTransactionRange.fulfilled,
      (state, action) => {
        if (action.payload.data?.products?.items?.length > 0) {
          state.dataProductMicroTransRange =
            action.payload.data?.products?.items;
        }
      }
    );
    builder.addCase(
      getProductMicroTransactionSingle.fulfilled,
      (state, action) => {
        if (action.payload.data?.products?.items?.length > 0) {
          state.dataProductMicroTransSingle =
            action.payload.data?.products?.items;
        }
      }
    );
    builder.addCase(switchStatusSquareSubsOnCart.pending, (state) => {
      state.loadingSwitchSubscriptions = true;
    });
    builder.addCase(switchStatusSquareSubsOnCart.fulfilled, (state, action) => {
      state.loadingSwitchSubscriptions = false;
      state.cart = action.payload;
      state.cartIdSkytrak = action.payload.id;
    });
    builder.addCase(signOut, () => ({ ...initialState }));
    builder.addMatcher(
      (action) => action.type.endsWith('/rejected'),
      (state, action) => {
        state.loadingAddress = false;
        state.loadingUpdateMiniCartSkytrak = false;
        state.loadingSwitchSubscriptions = false;
      }
    );
  },
});

export const {
  changeDefaultAddressSkytrak,
  changeChooseCustomerAddressShippingSkytrakId,
  changeChooseCustomerAddressBillingSkytrakId,
  changeDataSquare,
  changeCartCloneSimulation,
} = shopSkytrakSlice.actions;

export const selectDefaultAddressSkytrak = (state: RootState) =>
  state.shop.defaultAddress;
export const selectDataProductByCategorySkytrak = (state: RootState) =>
  state.shopSkytrak.dataByCategory;
export const selectCustomerSkytrak = (state: RootState) =>
  state.shopSkytrak.customer;
export const selectLoadingAddressSkytrak = (state: RootState) =>
  state.shopSkytrak.loadingAddress;
export const selectCustomerCartSkytrak = (state: RootState) =>
  state.shopSkytrak.cart;
export const selectCustomerAddressShippingSkytrakId = (state: RootState) =>
  state.shopSkytrak.chooseCustomerAddressShippingId;
export const selectCustomerAddressBillingSkytrakId = (state: RootState) =>
  state.shopSkytrak.chooseCustomerAddressBillingId;
export const selectCustomerAddressesSkytrak = (state: RootState) =>
  state.shopSkytrak.customer?.addresses;
export const selectCountriesData = (state: RootState) =>
  state.shopSkytrak.countriesData;
export const selectDataProductSkytrak = (state: RootState) =>
  state.shopSkytrak.dataProductSkytrak;
export const selectDataSquare = (state: RootState) =>
  state.shopSkytrak.squareData;
export const selectDataSimulationProductSkytrak = (state: RootState) =>
  state.shopSkytrak.dataSimulationProductSkytrak;
export const selectLoadingUpdateMiniCartSkytrak = (state: RootState) =>
  state.shopSkytrak.loadingUpdateMiniCartSkytrak;
export const selectCartIdSkytrak = (state: RootState) =>
  state.shopSkytrak.cartIdSkytrak;
export const selectLoadingDeactiveCart = (state: RootState) =>
  state.shopSkytrak.loadingDeactiveCart;
export const selectLoadingCreateEmptyCart = (state: RootState) =>
  state.shopSkytrak.loadingCreateEmptyCart;
export const selectLoadingSwitchSubscriptions = (state: RootState) =>
  state.shopSkytrak.loadingSwitchSubscriptions;
export const selectCartCloneSimulation = (state: RootState) =>
  state.shopSkytrak.cartCloneSimulation;
export const selectProductMicroTransPack = (state: RootState) =>
  state.shopSkytrak.dataProductMicroTransPack;
export const selectProductMicroTransBundle = (state: RootState) =>
  state.shopSkytrak.dataProductMicroTransBundle;
export const selectProductMicroTransRange = (state: RootState) =>
  state.shopSkytrak.dataProductMicroTransRange;
export const selectProductMicroTransSingle = (state: RootState) =>
  state.shopSkytrak.dataProductMicroTransSingle;

export default shopSkytrakSlice.reducer;
